'use client';

import { WaitlistCTA } from '@/components/marketing/WaitlistCTA';
import { Skeleton } from '@/components/ui/skeleton';
import { theme } from '@/lib/ui/theme';

interface HeroSectionProps {
  isLoading?: boolean;
}

export function HeroSection({ isLoading = false }: HeroSectionProps) {
  if (isLoading) {
    return (
      <section
        className="px-6 pt-24 pb-10 md:px-16 md:pt-32 md:pb-12"
        style={{ background: 'var(--hero-background)' }}
      >
        <div className="mx-auto max-w-[1400px] text-center">
          <Skeleton className="mx-auto mb-8 h-12 w-[85%] md:h-16" />
          <Skeleton className="mx-auto mb-4 h-7 w-[60%]" />
          <Skeleton className="mx-auto mb-3 h-6 w-[70%]" />
          <div className="mx-auto mb-10 max-w-[840px] space-y-2">
            <Skeleton className="mx-auto h-5 w-full" />
            <Skeleton className="mx-auto h-5 w-[80%]" />
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
            <div
              className="bg-muted inline-flex items-center justify-center rounded-md"
              style={{ padding: '1rem 2rem' }}
            >
              <Skeleton className="h-7 w-46 rounded" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="px-6 pt-24 pb-10 md:px-16 md:pt-32 md:pb-12"
      style={{ background: 'var(--hero-background)' }}
    >
      <div className="mx-auto max-w-[1400px] text-center">
        <h1
          className="text-gradient-hero mb-6 text-4xl leading-tight font-bold tracking-tight md:text-5xl lg:text-6xl"
          style={{ fontFamily: theme.fonts.display }}
        >
          AI doesn't fail loudly. It fails confidently.
        </h1>
        <p
          className="mb-4 text-xl md:text-2xl"
          style={{ fontFamily: theme.fonts.body }}
        >
          AI increased code throughput. It did not increase engineering
          judgment.
        </p>
        <p
          className="mx-auto mb-3 max-w-[760px] text-lg md:text-xl"
          style={{ fontFamily: theme.fonts.body }}
        >
          Whatever agent writes the code, Mindrealm catches what it gets
          confidently wrong.
        </p>
        <p
          className="text-muted-foreground mx-auto mb-8 max-w-[840px] text-base md:text-lg"
          style={{ fontFamily: theme.fonts.body }}
        >
          75+ deterministic checks across Go, Python, TypeScript, and Rust. On
          Claude Code, Codex, Kimi Code, and Antigravity CLI, Mindrealm sends
          findings back for the agent to fix, then reviews the change again.
        </p>
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
          <WaitlistCTA label="Install the GitHub App" source="hero" />
        </div>
      </div>
    </section>
  );
}
